/* Scent quiz (multiple choice) — engine and rendering.

   SEPARATE FROM THE SCENT AI CONVERSATION. Different page, different template,
   no shared runtime. Nothing here touches assets/scent-ai.js.

   WHAT LIVES WHERE, AND WHY. The scent model below — which families and smells
   each EDP carries — is OURS, derived from Sheet 5b of Scent-AI-model.xlsx. It
   is not merchandising data a shopper can see, so it lives in the theme rather
   than in metafields. Everything a shopper DOES see — title, price, image, URL,
   availability — is read live from the catalogue by the section and handed over
   in a JSON blob. Seven of ten titles in the design handoff were already stale
   against live, which is why nothing displayable is hardcoded here.

   `filter.scent_family` is deliberately NOT the source. Audited 16 Sept: twenty
   distinct values across eleven products, three casings of the same word
   (FLORAL/Floral/floral), families mixed with single notes (Gardenia, Orris,
   Sandalwood), and This Love of Mine carrying no family at all. Sheet 5b is the
   signed-off model; the metafield is a storefront filter that drifted. */

(function () {
  'use strict';

  /* ---- THE SCENT MODEL ----------------------------------------------------
     families: Sheet 5b's own taxonomy. `Unisex` is excluded by decision — it
     is a positioning rather than a smell, and Q1 already asks femme/unisex.

     Tags marked ADDED were completed from 5b's note pyramids on 16 Sept
     (lever 1 of the bestseller work). Each carries its evidence, because a tag
     with no source is how a quiz starts lying. */
  var MODEL = {
    'afterparty-edp-50ml': {
      gender: 'femme',
      family: ['Sweet', 'Musk/Wood', 'Vanilla', 'Floral'],
      /* Vanilla ADDED — 5b: "Praline and vanilla are the signature".
         Floral ADDED — character line "Sweet sparkles and dark florals";
         heart is cassis and ylang-ylang. */
      smell: ['grown up gourmand', 'warm and musky', "sweet 'n silky"],
      /* "sweet 'n silky" ADDED — a praline heart over warm woods and vanilla
         is the silky end of sweet, not the sharp end. */
      style: ['crazy sexy cool'], mood: ['social media', 'make-up']
    },
    'lost-in-dusk-50ml-eau-de-parfum': {
      gender: 'femme',
      family: ['Vanilla', 'Musk/Wood', 'Sweet'],
      /* Sweet ADDED — 5b's own axis row names it: "Lucky Chaos, Afterparty,
         Lost in Dusk are sweet." */
      smell: ['grown up gourmand', 'warm and musky'],
      /* CORRECTED 16 Sept: was `solar amber`, which contradicted 5b's "Dark
         gourmand. Coffee is the distinctive note" and its rules-out line
         ("light, fresh or clean"). Coffee over vanilla bourbon is gourmand. */
      style: ['crazy sexy cool'], mood: ['rush', 'make-up']
    },
    'lhotel-eau-de-parfum-50ml': {
      gender: 'femme',
      family: ['Floral', 'Vanilla', 'Musk/Wood', 'Sweet'],
      /* Sweet ADDED — filter.scent_family carries GOURMAND and 5b calls it a
         "creamy floral vanilla". FLAGGED: 5b's sweet-axis row does not list
         L'Hotel among the sweet ones, so this one tag is a judgement call. */
      smell: ["sweet 'n silky", 'grown up gourmand'],
      style: ['polished'], mood: ['make-up', 'coffee']
    },
    'this-love-of-mine-eau-de-parfum-50ml': {
      gender: 'femme',
      family: ['Floral', 'Musk/Wood'],
      /* Musk/Wood ADDED — 5b base: sandalwood, musk. */
      smell: ['white florals', 'warm and musky'],
      /* `a mixed bouquet of flowers` REMOVED by decision: 5b distinguishes this
         from Joyeux precisely on it — "The biggest white florals, unapologetic"
         against Joyeux "lighter and greener". It should not compete with the
         scent 5b calls the mixed bouquet. */
      style: ['boho'], mood: ['meditate', 'coffee']
    },
    'lucky-chaos-50ml-eau-de-parfum': {
      gender: 'femme',
      family: ['Sweet', 'Vanilla', 'Fruity'],
      /* Given verbatim in the brief. The only Sweet EDP, and absent from the
         design handoff entirely — it went live 7 September. */
      smell: ['grown up gourmand', "sweet 'n silky"],
      /* "salted rather than sugary" describes the sweetness, not the texture;
         milk, white musk and vanilla are silky. */
      style: ['playful', 'crazy sexy cool'], mood: ['make-up', 'coffee']
      /* style and mood here are ASSIGNED, not sourced — no sheet gives them.
         They carry +1 each, so they colour the ranking without deciding it. */
    },
    'at-night-we-dance-musky-spice-eau-de-parfum': {
      gender: 'unisex',
      family: ['Musk/Wood', 'Citrus'],
      /* Magnolia is one note of four in the heart — not enough for Floral. */
      smell: ['rich smoky wood', 'warm and musky'],
      style: ['streetwear'], mood: ['rush']
    },
    'call-me-venus-eau-de-parfum-50ml': {
      gender: 'femme',
      family: ['Oud', 'Rose', 'Musk/Wood'],
      smell: ['rich smoky wood', 'warm and musky'],
      /* CORRECTED 16 Sept: was `solar amber`. There is no amber anywhere in its
         pyramid — it dries to labdanum, woods and musk, which is exactly warm
         and musky. */
      style: ['streetwear', 'crazy sexy cool'], mood: ['rush', 'make-up']
    },
    'undone-50ml-eau-de-parfum': {
      gender: 'unisex',
      family: ['Fruity', 'Musk/Wood'],
      /* Bitter orange is an opening note only; 5b reserves Citrus for At Night
         We Dance and Sun-kissed, where it is structural. */
      smell: ['summery orchards', 'solar amber', "sweet 'n silky"],
      /* solar amber KEPT and now the ONLY holder of that tag — genuinely bright
         with amber underneath. "sweet 'n silky" ADDED: orris, praline and
         coconut is a creamy heart. */
      style: ['playful', 'athleisure'], mood: ['train', 'social media']
    },
    'stardust-eau-de-parfum-50ml': {
      gender: 'unisex',
      family: ['Musk/Wood'],
      /* Honestly a one-family scent: 5b gives Musk/Wood and Unisex, and Unisex
         is excluded. Adding anything sweet would contradict its rules-out line
         ("sweet, gourmand or obviously feminine"). */
      smell: ['woody but light', 'warm and musky'],
      style: ['polished', 'streetwear'], mood: ['rush', 'train']
    },
    'sun-kissed-eau-de-parfum-50ml': {
      gender: 'femme',
      family: ['Fruity', 'Citrus', 'Musk/Wood'],
      smell: ['summery orchards', 'woody but light'],
      style: ['playful', 'athleisure'], mood: ['train', 'coffee']
    },
    'joyeux-eau-de-parfum-peony-50ml': {
      gender: 'femme',
      family: ['Floral', 'Fruity', 'Musk/Wood'],
      /* Musk/Wood ADDED — 5b base: sandalwood, white musk. */
      smell: ['a mixed bouquet of flowers', 'summery orchards'],
      style: ['boho', 'playful'], mood: ['social media', 'coffee']
    }
  };

  /* Ties are resolved toward these. A tie means the model cannot separate two
     scents on what she told us, so resolving it commercially costs her nothing
     — it is NOT a multiplier, and it never beats a genuine higher score. */
  var BESTSELLERS = [
    'lucky-chaos-50ml-eau-de-parfum',
    'lhotel-eau-de-parfum-50ml',
    'afterparty-edp-50ml',
    'lost-in-dusk-50ml-eau-de-parfum',
    'this-love-of-mine-eau-de-parfum-50ml'
  ];

  /* WEIGHTS. Family and smell decide; everything else colours.
     5 + 5 = 10 against 1 + 1 + 1 = 3, so the two deciding answers together
     always outrank the other three combined — which is the point. */
  var W = { family: 5, smell: 5, gender: 1, style: 1, mood: 1 };

  var QUESTIONS = [
    { field: 'gender', cols: 2, title: 'Do you prefer scents that are',
      options: [ { key: 'femme', label: 'Femme' }, { key: 'unisex', label: 'Unisex' } ] },
    { field: 'family', cols: 3, title: 'Which fragrance family are you drawn to?',
      /* Eight tiles in a 3-column grid, Oud last and the ninth cell empty.
         Four across leaves 2.5px of clearance on "Musk/Wood" at 390 — the same
         non-margin that forced the bundle bar's type step-down. Three across
         also keeps the rhythm: every question after Q1 is 3 columns. */
      options: [
        { key: 'Sweet', label: 'Sweet' }, { key: 'Musk/Wood', label: 'Musk/Wood' },
        { key: 'Floral', label: 'Floral' }, { key: 'Vanilla', label: 'Vanilla' },
        { key: 'Fruity', label: 'Fruity' }, { key: 'Citrus', label: 'Citrus' },
        { key: 'Rose', label: 'Rose' }, { key: 'Oud', label: 'Oud' } ] },
    { field: 'style', cols: 3, title: 'Which best describes your style?',
      options: [
        { key: 'playful', label: 'Playful' }, { key: 'streetwear', label: 'Streetwear' },
        { key: 'boho', label: 'Boho' }, { key: 'crazy sexy cool', label: 'Crazy sexy cool' },
        { key: 'polished', label: 'Polished' }, { key: 'athleisure', label: 'Athleisure' } ] },
    { field: 'mood', cols: 3, title: 'What does your morning look like?',
      options: [
        { key: 'coffee', label: 'Coffee' }, { key: 'meditate', label: 'Meditate' },
        { key: 'rush', label: 'Rush' }, { key: 'social media', label: 'Social media' },
        { key: 'train', label: 'Train' }, { key: 'make-up', label: 'Make-up' } ] },
    { field: 'smell', cols: 3, tightLabels: true, title: 'Choose your first instinctive fave smell',
      /* tightLabels steps Q5 down to 11.5px. "A mixed bouquet of flowers" is 26
         characters and needs three lines in a 94px tile at 13px, which makes
         that row taller than the other two. At 11.5px every one of the nine
         lands on two lines and the grid stays even — the same step-down the
         bundle bar pills took, and for the same reason. The wording is left
         alone: these nine are the live quiz's own language. */
      options: [
        { key: 'summery orchards', label: 'Summery orchards' },
        { key: 'a mixed bouquet of flowers', label: 'A mixed bouquet of flowers' },
        { key: "sweet 'n silky", label: "Sweet 'n silky" },
        { key: 'warm and musky', label: 'Warm and musky' },
        { key: 'rich smoky wood', label: 'Rich smoky wood' },
        { key: 'woody but light', label: 'Woody but light' },
        { key: 'white florals', label: 'White florals' },
        { key: 'solar amber', label: 'Solar amber' },
        { key: 'grown up gourmand', label: 'Grown up gourmand' } ] }
  ];
  var STEPS = QUESTIONS.length + 1;   /* five questions, then email capture */

  function has(list, key) { return list && list.indexOf(key) !== -1; }

  function score(handle, answers) {
    var m = MODEL[handle];
    if (!m) return 0;
    var s = 0;
    if (answers.gender && m.gender === answers.gender) s += W.gender;
    if (answers.family && has(m.family, answers.family)) s += W.family;
    if (answers.style && has(m.style, answers.style)) s += W.style;
    if (answers.mood && has(m.mood, answers.mood)) s += W.mood;
    if (answers.smell && has(m.smell, answers.smell)) s += W.smell;
    return s;
  }

  /* Rank every product the section handed us. Products with no model entry are
     dropped rather than scored zero — a mist or a set that wandered into the
     collection should not appear as a match at all. */
  function rank(products, answers) {
    return products
      .filter(function (p) { return MODEL[p.handle]; })
      .map(function (p) { return { p: p, s: score(p.handle, answers) }; })
      .sort(function (a, b) {
        if (b.s !== a.s) return b.s - a.s;
        var ab = BESTSELLERS.indexOf(a.p.handle), bb = BESTSELLERS.indexOf(b.p.handle);
        if (ab === -1 && bb === -1) return 0;
        if (ab === -1) return 1;
        if (bb === -1) return -1;
        return ab - bb;          /* earlier in the bestseller list wins */
      })
      .map(function (x) { return x.p; });
  }

  document.querySelectorAll('[data-sq]').forEach(function (root) {
    var blob = root.querySelector('[data-sq-data]');
    if (!blob) return;
    var DATA;
    try { DATA = JSON.parse(blob.textContent); } catch (e) { return; }
    var PRODUCTS = DATA.products || [];
    var COPY = DATA.copy || {};
    var TILE = DATA.tiles || {};
    var ALTS_PER_PAGE = 2;

    var state = { screen: 'hero', step: 1, answers: {}, altPage: 0, ranked: null };

    /* ---- SURVIVING THE OMNISEND REDIRECT ---------------------------------
       Omnisend can be configured to redirect back to this page after signup.
       That is a full page load, and the answers live only in memory — without
       this she would sign up and land back on the hero with the quiz gone,
       which is worse than not redirecting at all.

       So the answers are mirrored into sessionStorage as she goes, along with a
       flag set the moment she reaches the signup step. On boot, if that flag is
       set and every question is answered, we skip straight to her match.

       sessionStorage, not localStorage: this should last for the round trip and
       not turn into a stale result a week later. Every access is wrapped —
       Safari private mode throws on write rather than returning null.

       An explicit ?sq_signup=1 on the redirect URL also triggers it and is the
       more precise signal; the flag is what makes a bare same-URL redirect work
       too, since that case is indistinguishable from a reload by URL alone. */
    var STORE_KEY = 'rb_scent_quiz';
    function persist() {
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify({
          answers: state.answers,
          awaitingSignup: state.awaitingSignup === true
        }));
      } catch (e) { /* private mode, quota — the quiz still works, it just
                       cannot survive a redirect */ }
    }
    function clearStore() { try { sessionStorage.removeItem(STORE_KEY); } catch (e) {} }
    function answeredEverything(a) {
      return QUESTIONS.every(function (q) { return a && a[q.field]; });
    }
    function restoreAfterSignup() {
      var saved = null;
      try { saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return false; }
      if (!saved || !answeredEverything(saved.answers)) return false;
      if (!saved.awaitingSignup && !/[?&]sq_signup=1/.test(location.search)) return false;
      state.answers = saved.answers;
      state.ranked = rank(PRODUCTS, state.answers);
      state.screen = 'results';
      state.altPage = 0;
      return true;
    }

    /* Choosing an option moves her on by itself — she never presses Next on a
       question. The pause is so the selection actually registers: the pink
       border and the filled radio need a beat on screen or the step just
       appears to jump. Any other navigation cancels a pending advance. */
    var ADVANCE_MS = 280;
    var advanceTimer = null;
    function cancelAdvance() { clearTimeout(advanceTimer); advanceTimer = null; }

    /* Signing up moves her to her match. Omnisend owns the form completely and
       injects its success step on submit rather than pre-rendering it, so there
       is no success node to watch for up front and no public event to bind.
       What IS reliable: the email field exists while the form is on screen and
       is gone once Omnisend swaps in its success step. So: arm on step 6, wait
       until we have actually seen the field, and advance the first time it
       disappears. A validation error leaves the field in place, so a failed
       submit does not move her. */
    /* How long her confirmation stays on screen before we move her on. A
       setting because it is a judgement call, not a constant: if the Omnisend
       form shows the discount CODE on success rather than only emailing it,
       moving her away quickly loses it. Seconds in, milliseconds here. */
    var SUCCESS_MS = Math.max(0, (DATA.success_delay || 2)) * 1000;

    /* TWO SIGNALS, WHICHEVER COMES FIRST. Relying on one was fine as a backstop
       to the redirect; as the only mechanism it needs more than that.

         1. the email field disappears — Omnisend swaps the form for its success
            step, and a validation error leaves the field in place, so a failed
            submit still does not move her.
         2. a step container other than step-0 appears. Omnisend names its steps
            `omnisend-form-<id>-step-N-container`; step-0 is the form, and this
            page was inspected to confirm step-0 is the ONLY one present before
            submit. Anything higher is a state it moved to.

       Either fires once, then the observer disconnects. */
    var omniObserver = null;
    var omniPoll = null;
    function watchOmnisend() {
      if (omniObserver) return;
      var host = el.email;                 /* the whole step, not just the mount */
      if (!host || typeof MutationObserver !== 'function') return;
      var sawField = false;
      var fired = false;

      /* VISIBLE, not merely present. This is what the first version got wrong:
         Omnisend does not necessarily REMOVE the form on success — it can leave
         it in the DOM and hide it behind its success step. A hidden input still
         matches querySelector, so "the field is gone" never became true and the
         quiz sat on step 6 after a real signup. */
      function visible(node) {
        return !!(node && (node.offsetWidth || node.offsetHeight || node.getClientRects().length));
      }
      function liveField() {
        var f = host.querySelectorAll('input[type="email"]');
        for (var i = 0; i < f.length; i++) { if (visible(f[i])) return true; }
        return false;
      }
      function laterStepVisible() {
        var steps = host.querySelectorAll('[class*="-step-"]');
        for (var i = 0; i < steps.length; i++) {
          if (!/-step-0-/.test(steps[i].className || '') && visible(steps[i])) return true;
        }
        return false;
      }
      function check() {
        if (fired) return;
        if (state.screen !== 'quiz' || state.step !== STEPS) return;
        if (liveField()) { sawField = true; return; }
        if (!sawField && !laterStepVisible()) return;
        fired = true;
        if (omniObserver) omniObserver.disconnect();
        clearInterval(omniPoll);
        setTimeout(function () { if (state.screen === 'quiz') finish(); }, SUCCESS_MS);
      }

      omniObserver = new MutationObserver(check);
      omniObserver.observe(host, { childList: true, subtree: true,
                                   attributes: true, attributeFilter: ['class', 'style', 'hidden'] });

      /* A click inside the form arms a poll as well. Omnisend may swap states
         without a mutation this observer sees — an inline style on a node it
         replaced wholesale, say — and a signup is worth more than one mechanism.
         Bounded, so it cannot run forever. */
      host.addEventListener('click', function () {
        if (fired || omniPoll) return;
        var until = Date.now() + 30000;
        omniPoll = setInterval(function () {
          check();
          if (fired || Date.now() > until) { clearInterval(omniPoll); omniPoll = null; }
        }, 300);
      }, true);

      check();
    }

    /* Warm every tile image while she is still on the hero, so no question ever
       paints empty. Fire-and-forget: nothing waits on these and a failure is
       silent, because a missing tile image is already handled by the fallback. */
    Object.keys(TILE).forEach(function (k) {
      if (!TILE[k]) return;
      var pre = new Image();
      pre.decoding = 'async';
      pre.src = TILE[k];
    });

    var el = {
      hero: root.querySelector('[data-sq-hero]'),
      quiz: root.querySelector('[data-sq-quiz]'),
      results: root.querySelector('[data-sq-results]'),
      title: root.querySelector('[data-sq-title]'),
      grid: root.querySelector('[data-sq-grid]'),
      email: root.querySelector('[data-sq-email]'),
      prev: root.querySelector('[data-sq-prev]'),
      next: root.querySelector('[data-sq-next]'),
      count: root.querySelector('[data-sq-count]'),
      primary: root.querySelector('[data-sq-primary]'),
      alts: root.querySelector('[data-sq-alts]'),
      altPage: root.querySelector('[data-sq-altpage]'),
      altPrev: root.querySelector('[data-sq-altprev]'),
      altNext: root.querySelector('[data-sq-altnext]')
    };

    function tileMarkup(field, opt, selected) {
      var img = TILE[field + '::' + opt.key];
      /* NOT lazy. The grid is rebuilt from scratch on every step, so a lazy tile
         image starts loading only once its question is already on screen — every
         Next click showed a grid of empty tiles that filled in a beat later.
         These are nine small images at most and they are the content of the
         question, not decoration below the fold. Paired with the preload below,
         which fetches every question's tiles once at start. */
      var media = img
        ? '<img class="sq-tile__img" src="' + img + '" alt="" decoding="async" width="200" height="140">'
        : '<span class="sq-tile__img sq-tile__img--empty" aria-hidden="true"></span>';
      return '<button type="button" role="radio" aria-checked="' + (selected ? 'true' : 'false') +
        '" class="sq-tile' + (selected ? ' sq-tile--on' : '') + '" data-sq-opt="' +
        encodeURIComponent(opt.key) + '">' +
        '<span class="sq-tile__radio" aria-hidden="true"></span>' + media +
        '<span class="sq-tile__label rb-t-b1">' + opt.label + '</span></button>';
    }

    function paintQuestion() {
      var q = QUESTIONS[state.step - 1];
      var isEmail = state.step === STEPS;

      el.title.innerHTML = isEmail
        ? (COPY.email_heading || '')
        : q.title + ' <span class="sq-req" aria-hidden="true">*</span>';
      el.grid.hidden = isEmail;
      el.email.hidden = !isEmail;

      if (!isEmail) {
        el.grid.className = 'sq-grid sq-grid--' + q.cols + (q.tightLabels ? ' sq-grid--tight' : '');
        el.grid.setAttribute('role', 'radiogroup');
        el.grid.setAttribute('aria-label', q.title);
        el.grid.innerHTML = q.options.map(function (o) {
          return tileMarkup(q.field, o, state.answers[q.field] === o.key);
        }).join('');
      }

      if (isEmail) { state.awaitingSignup = true; persist(); watchOmnisend(); }

      el.count.textContent = state.step + ' of ' + STEPS;
      el.prev.textContent = state.step === 1 ? (COPY.back || '‹ Back') : (COPY.prev || '‹ Previous');
      el.next.textContent = isEmail ? (COPY.submit || 'Submit ✓') : (COPY.next || 'Next ›');
      /* The email step is skippable by design, so Next is never disabled there. */
      el.next.disabled = !isEmail && !state.answers[q.field];
    }

    function chips(p) {
      var m = MODEL[p.handle];
      if (!m) return '';
      /* Families, not product tags. The handoff drew tag chips from Shopify
         tags, which on this catalogue are campaign labels ("New", "spo-enabled",
         and the product's own name) rather than anything about the smell. */
      return '<ul class="sq-chips">' + m.family.map(function (f) {
        return '<li class="sq-chip rb-t-l1">' + f + '</li>';
      }).join('') + '</ul>';
    }

    function card(p, kind) {
      var img = p.image
        ? '<img class="sq-card__img" src="' + p.image + '" alt="' + p.title + '" width="600" height="600">'
        : '';
      var buy = kind === 'primary'
        ? '<div class="sq-card__buy">' +
            '<button type="button" class="sq-btn sq-btn--solid rb-t-ui1" data-sq-add="' + p.variant + '">' +
              (COPY.add || 'Add To Cart') + '</button>' +
            '<a class="sq-btn sq-btn--ghost rb-t-ui1" href="' + p.url + '">' + (COPY.buy || 'Buy now') + '</a>' +
          '</div>'
        : '<a class="sq-btn sq-btn--ghost rb-t-ui1" href="' + p.url + '">' + (COPY.view || 'View') + '</a>';
      /* Everything except the media is wrapped in __body so the desktop card can
         be a two-column grid of exactly TWO children. Placing six siblings in a
         grid instead — even with explicit columns — makes the media span five
         rows and distribute its height across them, which opened a gap under
         the title that no amount of row sizing closed cleanly. */
      return '<div class="sq-card sq-card--' + kind + '">' +
        '<a class="sq-card__media" href="' + p.url + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
        '<div class="sq-card__body">' +
          '<h3 class="sq-card__title rb-t-ui3"><a href="' + p.url + '">' + p.title + '</a></h3>' +
          (kind === 'primary' ? '<p class="sq-card__desc rb-t-b3">' + (p.description || '') + '</p>' : '') +
          chips(p) + buy +
        '</div></div>';
    }

    function paintResults() {
      var ranked = state.ranked || [];
      if (!ranked.length) return;
      el.primary.innerHTML = card(ranked[0], 'primary');

      /* Lever 3: lead the alternates with bestsellers. Within the ranked
         remainder only — this reorders equals, it never promotes a worse match
         above a better one, because `rank` has already sorted by score. */
      var rest = ranked.slice(1);
      var pages = Math.max(1, Math.ceil(rest.length / ALTS_PER_PAGE));
      if (state.altPage >= pages) state.altPage = 0;
      if (state.altPage < 0) state.altPage = pages - 1;
      var from = state.altPage * ALTS_PER_PAGE;
      el.alts.innerHTML = rest.slice(from, from + ALTS_PER_PAGE)
        .map(function (p) { return card(p, 'alt'); }).join('');
      el.altPage.textContent = (state.altPage + 1) + ' ' + (COPY.out_of || 'Out of') + ' ' + pages;
      el.altPrev.disabled = pages < 2;
      el.altNext.disabled = pages < 2;
    }

    /* The hero video keeps playing behind a display:none screen, and the mute
       control means it may be AUDIBLE by then — she unmutes, starts the quiz,
       and the sound follows her. Paused on the way out, resumed if she comes
       back. rb-video-tile owns loading and autoplay; this only owns the
       transition, so it never assigns a src or fights that script.

       play() returns a promise that rejects if autoplay is refused — caught,
       because an unhandled rejection here would surface as a console error on
       a page that is otherwise working. */
    function heroVideo(playing) {
      var v = el.hero && el.hero.querySelector('video');
      if (!v) return;
      if (playing) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      else if (!v.paused) { v.pause(); }
    }

    function paint() {
      heroVideo(state.screen === 'hero');
      el.hero.hidden = state.screen !== 'hero';
      el.quiz.hidden = state.screen !== 'quiz';
      el.results.hidden = state.screen !== 'results';
      if (state.screen === 'quiz') paintQuestion();
      if (state.screen === 'results') paintResults();
    }

    function finish() {
      state.ranked = rank(PRODUCTS, state.answers);
      state.screen = 'results';
      state.altPage = 0;
      paint();
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    root.addEventListener('click', function (e) {
      var t;

      if ((t = e.target.closest('[data-sq-start]'))) {
        state.screen = 'quiz'; state.step = 1; paint(); return;
      }
      if ((t = e.target.closest('[data-sq-opt]'))) {
        var q = QUESTIONS[state.step - 1];
        state.answers[q.field] = decodeURIComponent(t.getAttribute('data-sq-opt'));
        persist();
        paintQuestion();
        cancelAdvance();
        advanceTimer = setTimeout(function () {
          advanceTimer = null;
          if (state.screen !== 'quiz') return;
          if (state.step < STEPS) { state.step++; paint(); }
        }, ADVANCE_MS);
        return;
      }
      if ((t = e.target.closest('[data-sq-prev]'))) {
        cancelAdvance();
        if (state.step <= 1) { state.screen = 'hero'; } else { state.step--; }
        paint(); return;
      }
      if ((t = e.target.closest('[data-sq-next]'))) {
        cancelAdvance();
        if (state.step === STEPS) { finish(); } else { state.step++; paint(); }
        return;
      }
      if ((t = e.target.closest('[data-sq-skip]'))) { e.preventDefault(); cancelAdvance(); finish(); return; }
      if ((t = e.target.closest('[data-sq-altprev]'))) { state.altPage--; paintResults(); return; }
      if ((t = e.target.closest('[data-sq-altnext]'))) { state.altPage++; paintResults(); return; }
      if ((t = e.target.closest('[data-sq-retake]'))) {
        cancelAdvance();
        clearStore();
        state = { screen: 'hero', step: 1, answers: {}, altPage: 0, ranked: null };
        paint(); root.scrollIntoView({ behavior: 'smooth', block: 'start' }); return;
      }
      if ((t = e.target.closest('[data-sq-add]'))) {
        var id = t.getAttribute('data-sq-add');
        if (!id) return;
        t.disabled = true;
        fetch(DATA.routes.cart_add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed ' + r.status);
          /* Same handoff as every other add on the site: open the drawer if the
             theme has one, otherwise refresh the cart. Both are globals from
             assets/mini-cart.js. */
          /* toggleCart(doClose = false) — see assets/mini-cart.js. Passing true
             CLOSES the drawer, which is what this used to do. No argument opens
             it. Calling fetchCart() as well so the drawer's contents are fresh
             even when the theme has no drawer to open. */
          if (typeof window.toggleCart === 'function') window.toggleCart();
          if (typeof window.fetchCart === 'function') window.fetchCart();
        }).catch(function () {
          t.textContent = COPY.add_failed || 'Could not add';
        }).then(function () { t.disabled = false; });
      }
    });

    /* Returning from the Omnisend redirect lands her on her match, not the
       hero. Falls through to the normal hero start when there is nothing
       stored, which is every other visit. */
    restoreAfterSignup();
    paint();
  });
})();
